import { ismInitSwiper } from './js-carousel.js';

// Cache commonly used elements
const searchWrapper = document.querySelector('.search-wrapper');
const searchResults = document.querySelector('.search-results');
const searchInput = document.querySelector('input[name="q"]');
const searchSpinner = document.querySelector('.search-spinner');
const resultsBox = document.querySelector('.results');
const resultsBoxInitialContent = resultsBox.innerHTML;

/**
 * Creates a debounced function that delays invoking the provided function until after the specified delay.
 *
 * @param {Function} fn - The function to debounce.
 * @param {number} [delay=300] - The number of milliseconds to delay. Defaults to 300ms.
 * @returns {Function} - A new debounced function.
 */
const debounce = (fn, delay = 300) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

const closeSearchResults = () => {
  if (!searchResults) return;
  const searchOverlay = document.querySelector('.search-overlay');

  // Animate closing transition
  searchResults.style.animation = 'swoosh-out 0.2s ease-out';
  if (searchOverlay) {
    searchOverlay.style.animation = 'swoosh-out 0.2s ease-out';
  }

  // Handle animation end event
  const handleAnimationEnd = () => {
    searchResults.classList.remove('active');
    searchResults.removeEventListener('animationend', handleAnimationEnd);

    // Remove the search overlay if it exists
    if (searchOverlay) {
      searchOverlay.remove();
    }
  };

  // Attach animation end event listener
  searchResults.addEventListener('animationend', handleAnimationEnd);

  // Fallback to ensure it closes even if animationend doesn’t fire
  setTimeout(handleAnimationEnd, 300);
};

const openSearchResults = () => {
  if (!searchResults) return;

  searchResults.classList.add('active');
  searchResults.style.animation = 'swoosh-in 0.15s ease-out';

  const headerWrapper = document.getElementById('transparentHeaderWrapper');
  if (headerWrapper) {
    headerWrapper.classList.add('scrolled');
  }

  // Add overlay only if it doesn’t already exist
  if (!document.querySelector('.search-overlay')) {
    const overlay = document.createElement('div');
    overlay.classList.add('search-overlay');
    overlay.style.animation = 'swoosh-in 0.15s ease-out';
    overlay.addEventListener('click', closeSearchResults);
    document.body.appendChild(overlay);
  }

  // Focus on the search input field
  searchInput.focus();
};

const fetchSuggestions = async (query, sectionId) => {
  try {
    const response = await fetch(
      `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&section_id=${encodeURIComponent(
        sectionId
      )}&resources[options][fields]=title,product_type,variants.title,variants.barcode,variants.sku`
    );
    const suggestions = await response.text();

    const parsedHtml = new DOMParser().parseFromString(suggestions, 'text/html');

    // Update product results if available
    resultsBox.innerHTML = parsedHtml.querySelector('.results')?.innerHTML || '';
    const swiperElement = document.querySelector('.swiper-search');
    ismInitSwiper(swiperElement);
  } catch (error) {
    console.error('Error fetching suggestions:', error);
  }
};

const handleSearchInput = debounce((event) => {
  const query = event.target.value.trim();
  const sectionId = event.target.getAttribute('data-section-id');

  // Sync the input value with the visible search field
  document.querySelector('.search-wrapper .base-input input').value = query;

  // Clear search results if query length is 0
  if (query.length < 2) {
    resultsBox.innerHTML = resultsBoxInitialContent;
    return;
  }

  // Fetch suggestions if query length is at least 3 characters
  if (query.length >= 2) {
    fetchSuggestions(query, sectionId);
  }
}, 300); // Delay of 300ms to prevent excessive API calls

const initSearch = () => {
  // Attach event listener to open search results
  document.querySelectorAll('.search-trigger').forEach((element) => {
    element.addEventListener('click', () => {
      openSearchResults();
    });
    element.addEventListener(
      'touchstart',
      () => {
        openSearchResults();
      },
      { passive: true }
    );
  });

  // Attach event listener to close search results
  document.querySelector('.search-cancel').addEventListener('click', (event) => {
    closeSearchResults();
  });

  // Attach input event listener for search field
  searchInput.addEventListener('input', handleSearchInput);
};

export { initSearch };
